import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { TodoItem, TodoList, Comment } from '../types/todo';
import { BaseNotification } from '../types/notifications';
import { getSignalRUrl } from '../api/api.config';

export interface SignalRCallbacks {
  onTaskCreated?: (data: { Task: TodoItem; ChangedBy: string }) => void;
  onTaskUpdated?: (data: { Task: TodoItem; ChangedBy: string }) => void;
  onTaskDeleted?: (data: { TaskId: number; ListId: number; ChangedBy: string }) => void;
  onTaskCompleted?: (data: { Task: TodoItem; ChangedBy: string }) => void;
  onTaskAssigned?: (data: { Task: TodoItem; AssignedTo: string; ChangedBy: string }) => void;
  onListCreated?: (data: { List: TodoList; ChangedBy: string }) => void;
  onListUpdated?: (data: { List: TodoList; ChangedBy: string }) => void;
  onListDeleted?: (data: { ListId: number; ChangedBy: string }) => void;
  onListShared?: (data: { List: TodoList; SharedWith: string; ChangedBy: string }) => void;
  onCommentAdded?: (data: { Comment: Comment; ListId: number; ChangedBy: string }) => void;
  onNewNotification?: (notification: BaseNotification) => void;
  onUnreadCountUpdated?: (data: { UnreadCount: number }) => void;
  onUserViewingTask?: (data: { TaskId: number; UserEmail: string }) => void;
  onUserStoppedViewingTask?: (data: { TaskId: number; UserEmail: string }) => void;
  onUserOnlineStatusChanged?: (data: { UserEmail: string; IsOnline: boolean }) => void;
  onOnlineUsersUpdated?: (onlineUsers: string[]) => void;
  onInvitationReceived?: (data: any) => void;
  onInvitationStatusChanged?: (data: any) => void;
  // Kanban events
  onTaskStatusChanged?: (data: { TaskId: number; OldStatus?: string; NewStatus?: string; ListId: number }) => void;
  onKanbanColumnsUpdated?: (data: { ListId: number; Config: any }) => void;
  onUserMovingTask?: (data: { TaskId: number; FromColumn?: string; ToColumn?: string; ListId: number }) => void;
  onUserStoppedMovingTask?: (data: { TaskId: number; ListId: number }) => void;
}

class SignalRService {
  private connection: HubConnection | null = null;
  private callbacks: SignalRCallbacks = {};
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private isConnecting = false;

  constructor() {
    // SignalR Service Constructor
  }

  /**
   * Initialize SignalR connection with authentication token
   */
  async connect(token: string): Promise<void> {
    if (this.isConnecting) {
      return;
    }

    if (this.connection && this.isConnected) {
      return;
    }

    this.isConnecting = true;

    try {
      const signalRUrl = getSignalRUrl();
      
      // Test the negotiate endpoint first
      try {
        const negotiateUrl = `${signalRUrl}/negotiate`;
        const response = await fetch(negotiateUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Negotiate endpoint failed:', errorText);
        }
      } catch (negotiateError) {
        console.error('Failed to test negotiate endpoint:', negotiateError);
      }
      
      this.connection = new HubConnectionBuilder()
        .withUrl(signalRUrl, {
          withCredentials: false,
          accessTokenFactory: () => token
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext: { previousRetryCount: number }) => {
            // Exponential backoff: 0s, 2s, 10s, 30s, null (stop retrying)
            if (retryContext.previousRetryCount === 0) return 0;
            if (retryContext.previousRetryCount === 1) return 2000;
            if (retryContext.previousRetryCount === 2) return 10000;
            if (retryContext.previousRetryCount === 3) return 30000;
            return null; // Stop retrying
          }
        })
        .configureLogging(LogLevel.Information)
        .build();

      // Set up event handlers
      this.setupEventHandlers();

      // Set up connection event handlers
      this.connection.onreconnecting(() => {
        this.isConnected = false;
      });

      this.connection.onreconnected(() => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
      });

      this.connection.onclose(() => {
        this.isConnected = false;
        this.isConnecting = false;
        
        // Attempt manual reconnection if automatic reconnection failed
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          setTimeout(() => {
            this.reconnectAttempts++;
            this.connect(token);
          }, 5000 * this.reconnectAttempts); // Progressive delay
        }
      });

      await this.connection.start();
      
      // Check if connection is still valid after start (could be null if disconnect was called)
      if (this.connection) {
        this.isConnected = true;
        this.reconnectAttempts = 0;
      } else {
        this.isConnected = false;
      }

    } catch (error) {
      // Provide more detailed error information
      if (error instanceof Error) {
        // Check for specific error types and provide appropriate error handling
        if (error.message.includes('502')) {
          console.error('502 Bad Gateway: Backend API server is not accessible');
          console.error('Possible causes:');
          console.error('1. Backend API server is not running');
          console.error('2. Backend API server is not accessible');
          console.error('3. Network connectivity issues');
          console.error('4. Proxy configuration issues');
        } else if (error.message.includes('404')) {
          console.error('404 Not Found: SignalR hub endpoint not found');
          console.error('Check that the backend API has SignalR configured at /hubs/todo');
        } else if (error.message.includes('401') || error.message.includes('403')) {
          console.error('Authentication error: Invalid or expired token');
        } else if (error.message.includes('SSL') || error.message.includes('certificate')) {
          console.error('SSL certificate error: Check HTTPS configuration');
        }
        
        // Provide guidance based on current environment
        console.error('Connection URL used:', getSignalRUrl());
        if (import.meta.env.DEV) {
          console.error('Development mode: Check that Vite proxy is configured correctly');
          console.error('Backend should be running at localhost:5001');
        } else {
          console.error('Production mode: Check that nginx proxy is configured correctly');
          console.error('Backend should be accessible at https://api.symphonytodo.com');
        }
      }
      
      this.isConnected = false;
      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  /**
   * Disconnect from SignalR hub
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        this.isConnecting = false;
        await this.connection.stop();
      } catch (error) {
        console.error('Error disconnecting from SignalR:', error);
      } finally {
        this.connection = null;
        this.isConnected = false;
        this.reconnectAttempts = 0;
        this.isConnecting = false;
      }
    }
  }

  /**
   * Set up all SignalR event handlers
   */
  private setupEventHandlers(): void {
    if (!this.connection) return;

    // Task events
    this.connection.on('TaskCreated', (data: any) => {
      this.callbacks.onTaskCreated?.(data);
    });
    this.connection.on('TaskUpdated', (data: any) => {
      this.callbacks.onTaskUpdated?.(data);
    });
    this.connection.on('TaskDeleted', (data: any) => {
      this.callbacks.onTaskDeleted?.(data);
    });
    this.connection.on('TaskCompleted', (data: any) => {
      this.callbacks.onTaskCompleted?.(data);
    });
    this.connection.on('TaskAssigned', (data: any) => {
      this.callbacks.onTaskAssigned?.(data);
    });

    // List events
    this.connection.on('ListCreated', (data: any) => {
      this.callbacks.onListCreated?.(data);
    });
    this.connection.on('ListUpdated', (data: any) => {
      this.callbacks.onListUpdated?.(data);
    });
    this.connection.on('ListDeleted', (data: any) => {
      this.callbacks.onListDeleted?.(data);
    });
    this.connection.on('ListShared', (data: any) => {
      this.callbacks.onListShared?.(data);
    });

    // Comment events
    this.connection.on('CommentAdded', (data: any) => {
      this.callbacks.onCommentAdded?.(data);
    });

    // Notification events
    this.connection.on('NewNotification', (notification: any) => {
      this.callbacks.onNewNotification?.(notification);
    });
    this.connection.on('UnreadCountUpdated', (data: any) => {
      this.callbacks.onUnreadCountUpdated?.(data);
    });

    // User presence events
    this.connection.on('UserViewingTask', (data: any) => {
      this.callbacks.onUserViewingTask?.(data);
    });
    this.connection.on('UserStoppedViewingTask', (data: any) => {
      this.callbacks.onUserStoppedViewingTask?.(data);
    });
    this.connection.on('UserOnlineStatusChanged', (data: any) => {
      this.callbacks.onUserOnlineStatusChanged?.(data);
    });
    this.connection.on('OnlineUsersUpdated', (onlineUsers: string[]) => {
      this.callbacks.onOnlineUsersUpdated?.(onlineUsers);
    });

    // Invitation events
    this.connection.on('InvitationReceived', (data: any) => {
      this.callbacks.onInvitationReceived?.(data);
    });
    this.connection.on('InvitationStatusChanged', (data: any) => {
      this.callbacks.onInvitationStatusChanged?.(data);
    });

    // Kanban events
    this.connection.on('TaskStatusChanged', (data: any) => {
      this.callbacks.onTaskStatusChanged?.(data);
    });
    this.connection.on('KanbanColumnsUpdated', (data: any) => {
      this.callbacks.onKanbanColumnsUpdated?.(data);
    });
    this.connection.on('UserMovingTask', (data: any) => {
      this.callbacks.onUserMovingTask?.(data);
    });
    this.connection.on('UserStoppedMovingTask', (data: any) => {
      this.callbacks.onUserStoppedMovingTask?.(data);
    });
  }

  /**
   * Register callbacks for SignalR events
   */
  setCallbacks(callbacks: SignalRCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Join a specific list group for real-time updates
   */
  async joinListGroup(listId: number): Promise<void> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke('JoinListGroup', listId);
      } catch (error) {
        console.error(`SignalR: Failed to join list group ${listId}:`, error);
      }
    }
  }

  /**
   * Leave a specific list group
   */
  async leaveListGroup(listId: number): Promise<void> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke('LeaveListGroup', listId);
      } catch (error) {
        console.error(`Failed to leave list group ${listId}:`, error);
      }
    }
  }

  /**
   * Indicate user is currently viewing a specific task
   */
  async viewingTask(taskId: number): Promise<void> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke('ViewingTask', taskId);
      } catch (error) {
        console.error(`Failed to indicate viewing task ${taskId}:`, error);
      }
    }
  }

  /**
   * Stop viewing a specific task
   */
  async stopViewingTask(taskId: number): Promise<void> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke('StopViewingTask', taskId);
      } catch (error) {
        console.error(`Failed to stop viewing task ${taskId}:`, error);
      }
    }
  }

  /**
   * Get list of currently online users
   */
  async getOnlineUsers(): Promise<void> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke('GetOnlineUsers');
      } catch (error) {
        console.error('Failed to get online users:', error);
      }
    }
  }

  /**
   * Get connection status
   */
  getConnectionStatus(): { isConnected: boolean; connectionId?: string } {
    return {
      isConnected: this.isConnected,
      connectionId: this.connection?.connectionId || undefined
    };
  }
}

// Export singleton instance
export const signalRService = new SignalRService();
